/** Consent is re-prompted after this period (milliseconds). */
export const ANALYTICS_CONSENT_TTL_MS = 90 * 24 * 60 * 60 * 1000;

const consentKey = (sellerID) => `prodinent_analytics_consent_${sellerID}`;

/** @typedef {'accepted' | 'rejected'} AnalyticsConsentValue */

/**
 * @returns {{ value: AnalyticsConsentValue, savedAt: number, expiresAt: number } | null}
 */
function readStoredConsent(sellerID) {
  if (!sellerID || typeof localStorage === "undefined") return null;

  try {
    const raw = localStorage.getItem(consentKey(sellerID));
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const value = parsed?.value;
    const expiresAt = Number(parsed?.expiresAt);

    if (value !== "accepted" && value !== "rejected") {
      localStorage.removeItem(consentKey(sellerID));
      return null;
    }

    if (!expiresAt || Date.now() > expiresAt) {
      localStorage.removeItem(consentKey(sellerID));
      return null;
    }

    return {
      value,
      savedAt: Number(parsed.savedAt) || expiresAt - ANALYTICS_CONSENT_TTL_MS,
      expiresAt,
    };
  } catch {
    try {
      localStorage.removeItem(consentKey(sellerID));
    } catch {
      /* ignore */
    }
    return null;
  }
}

/**
 * @returns {AnalyticsConsentValue | null}
 */
export function getAnalyticsConsent(sellerID) {
  return readStoredConsent(sellerID)?.value ?? null;
}

/** @param {AnalyticsConsentValue} value */
export function setAnalyticsConsent(sellerID, value) {
  if (!sellerID || typeof localStorage === "undefined") return;
  if (value !== "accepted" && value !== "rejected") return;

  try {
    const savedAt = Date.now();
    localStorage.setItem(
      consentKey(sellerID),
      JSON.stringify({
        value,
        savedAt,
        expiresAt: savedAt + ANALYTICS_CONSENT_TTL_MS,
      })
    );
  } catch {
    /* ignore */
  }
}

export function clearAnalyticsConsent(sellerID) {
  if (!sellerID || typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(consentKey(sellerID));
  } catch {
    /* ignore */
  }
}

export function hasAnalyticsConsentAccepted(sellerID) {
  return getAnalyticsConsent(sellerID) === "accepted";
}

/** Days until stored consent expires; null if none or expired. */
export function getAnalyticsConsentDaysRemaining(sellerID) {
  const stored = readStoredConsent(sellerID);
  if (!stored) return null;
  const msLeft = stored.expiresAt - Date.now();
  return Math.max(0, Math.ceil(msLeft / (24 * 60 * 60 * 1000)));
}
